import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import CustomerOrderStatusBadge from '#/modules/crmMgmt/customerOrder/components/CustomerOrderStatusBadge';
import {
  displayOrDash,
  mapCustomerOrderMeta,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';

function CustomerOrderHero({ meta, actions, compact = false }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1
              className={cn(
                'font-bold text-gray-900',
                compact ? 'text-xl' : 'text-2xl',
              )}
            >
              {meta.code}
            </h1>
            {meta.status ? <CustomerOrderStatusBadge status={meta.status} /> : null}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            客戶：
            {meta.customerSubtitle}
          </p>
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

CustomerOrderHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
    customerSubtitle: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
  compact: PropTypes.bool,
};

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function CustomerOrderInfoGrid({ meta, compact = false }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        !compact && 'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <HistoryField label="建立" value={meta.createDate} />
      <HistoryField label="修改" value={meta.reviseDate} />
      <HistoryField label="結案" value={meta.closeDate} />
      <HistoryField label="中止" value={meta.terminatedDate} />

      <HistoryField label="訂單日" value={meta.customerOrderDate} />
      <HistoryField label="幣別" value={meta.currencyCode} />
      <HistoryField label="課稅別" value={meta.taxType} />
      <HistoryField label="稅率" value={meta.taxRate} />

      <HistoryField label="提交" value={meta.submittedDate} />
      <HistoryField label="核准" value={meta.approvedDate} />
      <HistoryField label="退回" value={meta.rejectedDate} />
      {!compact ? (
        <div className="hidden md:block" aria-hidden="true" />
      ) : null}
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <p className="text-xs text-gray-500">訂單事由</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.issueReason)}
        </p>
      </div>
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

CustomerOrderInfoGrid.propTypes = {
  meta: PropTypes.object.isRequired,
  compact: PropTypes.bool,
};

const CustomerOrderMainInfoCard = ({
  detail,
  embedded = false,
  nested = false,
  compact = false,
  actions = null,
  containerClassName = '',
}) => {
  const meta = mapCustomerOrderMeta(detail);
  const surfacePaddingClassName = compact ? 'p-4' : 'p-5';

  const content = (
    <>
      <CustomerOrderHero meta={meta} actions={actions} compact={compact} />
      <CustomerOrderInfoGrid meta={meta} compact={compact} />
    </>
  );

  if (nested) {
    return content;
  }

  if (embedded) {
    return (
      <div className={cn('border-b border-slate-200', surfacePaddingClassName)}>
        {content}
      </div>
    );
  }

  return (
    <div className={cn('receipt-card-elevate', containerClassName)}>
      <div className={cn('receipt-card-surface', surfacePaddingClassName)}>
        {content}
      </div>
    </div>
  );
};

CustomerOrderMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  nested: PropTypes.bool,
  compact: PropTypes.bool,
  actions: PropTypes.node,
  containerClassName: PropTypes.string,
};

export default CustomerOrderMainInfoCard;
